import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {motion} from 'framer-motion'
import moment from 'moment'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {forwardRef, useImperativeHandle, useMemo, useRef} from 'react'
import {ReactComponent as MdSick} from '../../../assets/icons/MdSick.svg'
import Avatar from '../../../assets/images/avatar.jpg'
import {ReactComponent as GiHealthNormal} from '../../../assets/images/GiHealthNormal.svg'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../../utils/BlockScrollsUtils'

import {Image} from 'primereact/image'
import {Tooltip} from 'primereact/tooltip'
import SendMessageForm from '../../message/SendMessageForm'
import './style.scss'

const ReservationDialog = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    const {translateDatatable, translateCommons, translateKeys} =
        TranslateUtils()

    const visible = useSignal(false)
    const appointmentData = useSignal({})
    const isShowMore = useSignal(false)

    const sendMessageRef = useRef(null)

    const handleShowForm = value => {
        blockOverflownScroll()

        if (value) {
            appointmentData.value = value
            visible.value = true
        }
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        appointmentData.value = {}
        visible.value = false
    }

    const patientInformation = useMemo(() => {
        return appointmentData.value?.userInfo || {}
    }, [appointmentData.value?.userInfo])

    const renderHeaderTemplate = () => {
        return (
            <div className="w-full text-xs md:text-sm lg:text-lg">
                <div className="flex flex-wrap-reverse align-items-center gap-3">
                    <span className="mr-2 font-normal flex align-items-center">
                        {translateCommons('Reservation of')}
                        <div className="text-primary font-bold mx-2">
                            {patientInformation?.fullName || '-'}
                        </div>
                    </span>
                    <span className="flex align-items-center">
                        <span>&bull;</span>
                        <i className="pi pi-desktop mx-2"></i>
                        <p>{translateCommons('MANUAL APPOINTMENT')}</p>
                    </span>
                </div>
                <hr />
            </div>
        )
    }

    const renderPatientInformation = () => {
        return (
            <div className="text-xs md:text-sm lg:text-lg">
                <h6 className="font-bold">
                    {translateCommons('Personal Detail')}
                </h6>
                <div className="border-1 border-300 border-round-xl mb-3">
                    <div className="p-2 flex align-items-center justify-content-between">
                        <div className="flex align-items-center">
                            <img
                                alt={'avatar'}
                                src={Avatar}
                                className={`p-2 bg-white border-circle`}
                                style={{width: '80px'}}
                            />
                            <div className="flex flex-column">
                                <h4 className="m-0 mb-2 font-semibold text-xs md:text-sm lg:text-lg">
                                    {patientInformation?.fullName || '-'}
                                </h4>
                                <div className="flex align-items-center">
                                    <p className="mb-0 mr-2">
                                        <i className="pi pi-phone mr-2"></i>
                                        {patientInformation?.phone &&
                                        patientInformation?.phone?.length > 9
                                            ? patientInformation?.phone
                                            : '-'}
                                    </p>
                                    <p className="mb-0">
                                        &bull;
                                        <i className="pi pi-envelope mx-2"></i>
                                        {patientInformation?.email || '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className="p-2 text-primary w-full text-left cursor-pointer select-none mb-2"
                        onClick={() => (isShowMore.value = !isShowMore.value)}>
                        <i
                            className={`${
                                isShowMore.value
                                    ? 'pi pi-angle-up'
                                    : 'pi pi-angle-down'
                            } mr-2`}></i>
                        {translateCommons('More information')}
                    </div>

                    {/* Instructions || Descriptions */}
                    {isShowMore.value && (
                        <motion.div layout className="p-2">
                            <h6 className="col-12 font-bold m-0 mt-2">
                                {translateCommons('General Information')}
                            </h6>
                            <div className="grid col-12">
                                <div className="md:col-6 lg:col-6 col-12">
                                    <div className="col-12">
                                        <p className="text-600 m-0 text-sm">
                                            {translateKeys('Full Name')}
                                        </p>
                                        <h5 className="m-0 mb-1">
                                            {patientInformation?.fullName ||
                                                '-'}
                                        </h5>
                                    </div>
                                </div>
                                <div className="md:col-6 lg:col-6 col-12">
                                    <div className="col-12">
                                        <p className="text-600 m-0 text-sm">
                                            {translateKeys('Date of Birth')}
                                        </p>
                                        <h5 className="m-0 mb-1">
                                            {' '}
                                            {patientInformation?.dob || '-'}
                                        </h5>
                                    </div>
                                </div>
                                <div className="md:col-6 lg:col-6 col-12">
                                    <div className="col-12">
                                        <p className="text-600 m-0 text-sm">
                                            {translateKeys('Gender')}
                                        </p>
                                        <h5 className="m-0 mb-1">
                                            {' '}
                                            {patientInformation?.genderValue ||
                                                '-'}
                                        </h5>
                                    </div>
                                </div>
                                <div className="md:col-6 lg:col-6 col-12">
                                    <div className="col-12">
                                        <p className="text-600 m-0 text-sm">
                                            {translateKeys('Phone Number')}
                                        </p>
                                        <h5 className="m-0 mb-1">
                                            {patientInformation?.phone || '-'}
                                        </h5>
                                    </div>
                                </div>
                                <div className="md:col-6 lg:col-6 col-12">
                                    <div className="col-12">
                                        <p className="text-600 m-0 text-sm">
                                            {translateKeys('Email')}
                                        </p>
                                        <h5 className="m-0 mb-1">
                                            {patientInformation?.email || '-'}
                                        </h5>
                                    </div>
                                </div>
                                <div className="md:col-6 lg:col-6 col-12">
                                    <div className="col-12">
                                        <p className="text-600 m-0 text-sm">
                                            {translateKeys('Address')}
                                        </p>
                                        <h5 className="m-0 mb-1">
                                            {patientInformation?.address || '-'}
                                        </h5>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>
        )
    }

    const renderAppointmentInformation = () => {
        return (
            <div className="text-xs md:text-sm lg:text-lg">
                <div className="flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
                    <h6 className="font-bold m-0">
                        {translateCommons('Appointment Information')}
                    </h6>
                    <Button
                        icon="pi pi-bell"
                        label={translateCommons('Send notification')}
                        className="p-button-outlined w-full md:w-max lg:w-max"
                        onClick={() =>
                            sendMessageRef.current.showForm(patientInformation)
                        }
                    />
                </div>
                <div className="grid col-12 border-1 border-300 border-round mb-3">
                    <div className="grid col-12 mb-2">
                        <div className="lg:col-4 md:col-4 col-12">
                            <div className="flex align-items-start w-full">
                                <div className="p-2 mr-2 bg-blue-50 border-round-lg text-500">
                                    <GiHealthNormal height={20} width={20} />
                                </div>
                                <div className="flex flex-column">
                                    <h6 className="m-0 text-500 uppercase text-sm md:text-sm lg:text-lg">
                                        {translateCommons('TYPE')}
                                    </h6>
                                    <p className="text-md font-semibold">
                                        {appointmentData.value
                                            ?.appointmentType || '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-4 md:col-4 col-12">
                            <div className="flex align-items-start w-full">
                                <div className="p-2 mr-2 bg-blue-50 border-round-lg text-500">
                                    <i
                                        className="pi pi-user"
                                        style={{
                                            fontSize: '20px',
                                        }}></i>
                                </div>
                                <div className="flex flex-column">
                                    <h6 className="m-0 text-500 uppercase text-sm md:text-sm lg:text-lg">
                                        DOCTOR
                                    </h6>
                                    <p className="text-md font-semibold">
                                        {appointmentData.value?.ownerName ||
                                            '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-4 md:col-4 col-12">
                            <div className="flex align-items-start w-full">
                                <div className="p-2 mr-2 bg-blue-50 border-round-lg text-500">
                                    <i
                                        className="pi pi-clock"
                                        style={{
                                            fontSize: '20px',
                                        }}></i>
                                </div>
                                <div className="flex flex-column">
                                    <h6 className="m-0 text-500 uppercase text-sm md:text-sm lg:text-lg">
                                        DATE AND TIME
                                    </h6>
                                    <p className="text-md font-semibold">
                                        {moment(
                                            appointmentData.value?.dateAndTime,
                                        ).format('MMM DD YYYY h:mm A') || '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* <div className="w-full mb-3">
                        <div className="m-2 p-3 surface-100 border-round-xl">
                            <h5 className="font-semibold m-0 text-700">
                                Reason
                            </h5>
                            <p className="">
                                {appointmentData.value?.description || '-'}
                            </p>
                        </div>
                    </div> */}
                    <div className="grid col-12">
                        <div className="col-12">
                            <h5 className="flex align-items-center font-semibold m-0 text-700 mb-2 text-sm md:text-sm lg:text-lg">
                                <MdSick className="mr-2" /> Diagnose
                            </h5>
                            <div className="flex flex-wrap align-items-center justify-content-center w-full">
                                {patientInformation?.pathways?.length &&
                                    Object.keys(
                                        patientInformation?.pathways,
                                    ).map(key => (
                                        <div
                                            key={key}
                                            className="pointer m-2 w-3rem">
                                            <Tooltip
                                                target={`.tooltip-pathway-patient-${key}`}
                                                position="top"
                                                content={
                                                    patientInformation.pathways[
                                                        key
                                                    ]?.name
                                                }
                                            />
                                            <div
                                                className={`flex justify-content-center align-items-center text-700 tooltip-pathway-patient-${key} bg-gray-50 hover:bg-gray-100 border-round-lg p-2`}>
                                                <Image
                                                    width="26"
                                                    height="26"
                                                    style={{
                                                        objectFit: 'cover',
                                                    }}
                                                    src={`${patientInformation.pathways[key]?.icon}`}
                                                />
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                        <div className="col-12">
                            <h5 className="font-semibold m-0 text-700 mb-2 text-sm md:text-sm lg:text-lg">
                                <i className="pi pi-calendar mr-2"></i>
                                Description
                            </h5>
                            <p className="">
                                {appointmentData.value?.description || '-'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Dialog
            header={renderHeaderTemplate()}
            headerClassName="pb-0 bg-white text-color"
            visible={visible.value}
            position={'right'}
            style={{width: '50vw'}}
            breakpoints={{'960px': '75vw', '641px': '100vw'}}
            onHide={handleHideForm}
            draggable={false}
            resizable={false}>
            <div
                className="px-1 text-sm md:text-md lg:text-lg"
                style={{
                    minHeight: '70vh',
                }}>
                {/* Personal Detail */}
                {renderPatientInformation()}

                {/* Appointment Information */}
                {renderAppointmentInformation()}
            </div>
            <div className="my-2">
                <Button
                    label={translateDatatable('Close').toUpperCase()}
                    icon="pi pi-times"
                    className="col-12 p-button-lg p-button-outlined font-bold mb-2"
                    onClick={handleHideForm}
                />
            </div>

            {/* Form Ref */}
            <SendMessageForm ref={sendMessageRef} />
        </Dialog>
    )
})

export default ReservationDialog
